'use client';

import { useLocale, useTranslations } from 'next-intl';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { Input } from '@/components/ui/Input';
import { SectionLoadingOverlay } from '@/components/ui/SectionLoadingOverlay';
import { getCountryFromLocale } from '@/lib/i18n/config';
import { useRouter } from '@/lib/i18n/navigation';
import { fetchGuestRepairList } from '@/lib/service/repairDetailsClient';
import { buildRepairDetailsPath } from '@/lib/service/repairDetailsQuery';

import styles from './ServiceStatusLookup.module.scss';

export function ServiceStatusLookup() {
  const t = useTranslations('service.repairDetails.lookup');
  const locale = useLocale();
  const router = useRouter();
  const countryCode = getCountryFromLocale(locale).toUpperCase();

  const [email, setEmail] = useState('');
  const [repairNumber, setRepairNumber] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [repairNumberError, setRepairNumberError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isProcessing) return;

    const trimmedEmail = email.trim();
    const trimmedRepairNumber = repairNumber.trim();
    const nextEmailError = trimmedEmail ? null : t('emailRequired');
    const nextRepairNumberError = trimmedRepairNumber ? null : t('repairNumberRequired');

    setEmailError(nextEmailError);
    setRepairNumberError(nextRepairNumberError);
    if (nextEmailError || nextRepairNumberError) return;

    setIsProcessing(true);
    setNotFound(false);
    setError(null);

    try {
      const result = await fetchGuestRepairList({
        countryCode,
        email: trimmedEmail,
        accessCode: trimmedRepairNumber,
      });

      if ((result.items?.length ?? 0) > 0) {
        router.push(buildRepairDetailsPath({ email: trimmedEmail, code: trimmedRepairNumber }));
        return;
      }

      setNotFound(true);
    } catch {
      setError(t('accessError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const isBusy = isProcessing;

  return (
    <section
      id="servicestatus"
      className={styles.root}
      data-testid="ServiceStatusLookup"
      aria-labelledby="service-status-lookup-title"
      aria-busy={isBusy}
    >
      <div className={styles.search}>
        <div className={styles.header}>
          <h1 id="service-status-lookup-title" className={styles.title}>
            {t('title')}
          </h1>
          <p className={styles.intro}>{t('hint')}</p>
          <p className={styles.intro}>{t('hintNote')}</p>
        </div>

        <div className={styles.formBlock}>
          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            <Input
              autoComplete="email"
              data-testid="service-status-email"
              error={emailError ?? undefined}
              fullWidth
              hasError={Boolean(emailError)}
              label={t('emailLabel')}
              layout="floating"
              name="email"
              onChange={(event) => {
                setEmail(event.target.value);
                if (emailError) setEmailError(null);
              }}
              required
              spellCheck={false}
              type="email"
              value={email}
            />
            <Input
              autoComplete="off"
              data-testid="service-status-repair-number"
              error={repairNumberError ?? undefined}
              fullWidth
              hasError={Boolean(repairNumberError)}
              label={t('repairNumberLabel')}
              layout="floating"
              name="repairNumber"
              onChange={(event) => {
                setRepairNumber(event.target.value);
                if (repairNumberError) setRepairNumberError(null);
              }}
              required
              spellCheck={false}
              type="text"
              value={repairNumber}
            />
            <div className={styles.formActions}>
              <Button
                className={styles.searchButton}
                data-testid="service-status-submit"
                disabled={isProcessing}
                iconAfter={<Icon aria-hidden="true" name="chevron-right" />}
                loading={isProcessing}
                type="submit"
                variant="secondary"
              >
                {t('submit')}
              </Button>
            </div>
          </form>
        </div>
      </div>

      {isBusy ? (
        <SectionLoadingOverlay isLoading transparent>
          <div className={styles.loaderPlaceholder} aria-hidden="true" />
        </SectionLoadingOverlay>
      ) : null}

      {error && !isBusy ? (
        <p className={styles.error} role="alert" data-testid="service-status-error">
          {error}
        </p>
      ) : null}

      {notFound && !isBusy ? (
        <p className={styles.empty} role="alert" data-testid="service-status-not-found">
          {t('notFound')}
        </p>
      ) : null}
    </section>
  );
}
